import { useEffect, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { TenantService } from "@praesto/contracts";
import {
  type DepartmentDTO,
  type HotelDTO,
  createDepartment,
  deleteDepartment,
  getUsersApi,
  inviteUser,
  listDepartments,
  listHotels,
  updateDepartment,
  updateHotel,
  updateTenantSettings,
  useCore,
} from "@praesto/core";
import {
  Button,
  Card,
  FormField,
  Input,
  Select,
  SpectrumBar,
  WordmarkAnimato,
} from "@praesto/ui-kit";
import { ROLE_LABEL, ROLE_ORDER } from "../people/roleLabels";
import { DepartmentMultiSelect } from "../people/DepartmentMultiSelect";

type Step = 1 | 2 | 3;

/** Upper bound on waiting for the live settings to confirm the wizard is done. */
const CONFIRM_TIMEOUT_MS = 10_000;

/**
 * Resolve once the live tenant settings no longer say onboarding "pending",
 * or after `ms`. The PATCH answers before the settings snapshot reaches this
 * tab; leaving for /home in between lets `OnboardingGuard`, still reading
 * "pending", send the owner straight back to the wizard's welcome screen.
 */
function onboardingConfirmed(tenant: TenantService, ms: number): Promise<void> {
  return new Promise((resolve) => {
    let finished = false;
    let unsub: (() => void) | null = null;
    const done = (): void => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      unsub?.();
      resolve();
    };
    const timer = setTimeout(done, ms);
    // onSettingsChange may call back synchronously, before `unsub` is set.
    unsub = tenant.onSettingsChange((s) => {
      if (s.onboarding?.status !== "pending") done();
    });
    if (finished) unsub();
  });
}

/**
 * Mandatory 3-step first-run wizard for a self-service owner
 * (`OnboardingGuard` redirects here while `settings.onboarding?.status ===
 * "pending"`). No skip button anywhere — finishing step 3 is the only way
 * out, which PATCHes `settings/general.onboarding = { status: "completed" }`
 * so the guard stops redirecting.
 *
 * Step 1 "Struttura": edits the real hotel `provisionTenant` already created.
 * Step 2 "Reparti": edits the sample departments `seedTenant` wrote
 * (`sample: true`); keeping one un-sets its `sample` flag so it survives the
 * later sample-data purge. Removing one soft-disables it (still `sample:
 * true` until purged — see `SampleDataBanner`).
 * Step 3 "Invita staff": reuses the existing `/api/users/invite` flow.
 */
export function OnboardingWizard() {
  const core = useCore();
  const t = core.i18n.t;
  const navigate = useNavigate();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };

  const [step, setStep] = useState<Step>(1);
  // The welcome screen is the ONE place the brand moves (WordmarkAnimato,
  // sequence J, once per browser). Not a splash: nothing waits for the
  // animation and the button is reachable from the first frame.
  const [welcomed, setWelcomed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [hotel, setHotel] = useState<HotelDTO | null>(null);
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");

  const [departments, setDepartments] = useState<ReadonlyArray<DepartmentDTO>>([]);
  const [newCode, setNewCode] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<(typeof ROLE_ORDER)[number]>("staff");
  const [inviteDepartments, setInviteDepartments] = useState<ReadonlyArray<string>>([]);
  const [invited, setInvited] = useState<ReadonlyArray<string>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      const { hotels } = await listHotels(api.apiBase, tok);
      const h = hotels[0] ?? null;
      if (cancelled) return;
      setHotel(h);
      if (h) {
        setName(h.name);
        setCity(h.city ?? "");
        setAddress(h.address ?? "");
        setPhone(h.phone ?? "");
        const { departments: depts } = await listDepartments(api.apiBase, h.id, tok);
        if (!cancelled) setDepartments(depts.filter((d) => d.status === "active"));
      }
    })()
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  async function saveStructure() {
    if (!hotel) {
      setStep(2);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateHotel(api.apiBase, hotel.id, { name, city, address, phone }, tok);
      setStep(2);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  function renameDept(code: string, displayName: string) {
    setDepartments((ds) => ds.map((d) => (d.code === code ? { ...d, displayName } : d)));
  }

  async function removeDept(code: string) {
    if (!hotel) return;
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await deleteDepartment(api.apiBase, hotel.id, code, tok);
      setDepartments((ds) => ds.filter((d) => d.code !== code));
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function addDept() {
    if (!hotel || !newCode || !newDisplayName) return;
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await createDepartment(
        api.apiBase,
        hotel.id,
        { code: newCode, displayName: newDisplayName, shifts: [] },
        tok,
      );
      setDepartments((ds) => [
        ...ds,
        { id: newCode, code: newCode, displayName: newDisplayName, shifts: [], status: "active" },
      ]);
      setNewCode("");
      setNewDisplayName("");
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function saveDepartments() {
    if (!hotel) {
      setStep(3);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      // Only the ones still flagged `sample` need the PATCH — real
      // (owner-added) departments never carry the flag.
      await Promise.all(
        departments
          .filter((d) => d.sample === true)
          .map((d) =>
            updateDepartment(
              api.apiBase,
              hotel.id,
              d.code,
              { displayName: d.displayName, sample: false },
              tok,
            ),
          ),
      );
      setStep(3);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function sendInvite() {
    if (!hotel || !inviteEmail) return;
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await inviteUser(
        api.apiBase,
        {
          email: inviteEmail,
          role: inviteRole,
          hotelId: hotel.id,
          departments: [...inviteDepartments],
        },
        tok,
      );
      setInvited((xs) => [...xs, inviteEmail]);
      setInviteEmail("");
      setInviteDepartments([]);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function finish() {
    setBusy(true);
    setError(null);
    try {
      const api = getUsersApi();
      const tok = await api.getAuthToken();
      await updateTenantSettings(api.apiBase, { onboarding: { status: "completed" } }, tok);
      await onboardingConfirmed(core.tenant, CONFIRM_TIMEOUT_MS);
      void navigate({ to: `/${tenantSlug}/home`, replace: true });
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!welcomed) {
    return (
      <div className="surface-night grid min-h-[calc(100dvh-2.75rem)] place-items-center bg-praesto-night p-8 text-praesto-paper sm:p-12">
        <div className="grid w-full max-w-[520px] justify-items-start gap-7">
          <WordmarkAnimato theme="light" height={44} once="onboarding" />
          <div className="grid gap-2.5">
            <h1 className="text-h2 font-bold text-praesto-paper">
              {t("onboarding.welcome.title")}
            </h1>
            <p className="max-w-[420px] text-base text-sidebar-muted">
              {t("onboarding.welcome.body")}
            </p>
          </div>
          <SpectrumBar value={100} dark />
          <Button variant="primary" size="lg" onDark onClick={() => setWelcomed(true)}>
            {t("onboarding.welcome.cta")}
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return <p className="p-6 text-caption text-fg-muted">{t("onboarding.loading")}</p>;
  }

  const steps: ReadonlyArray<{ n: Step; label: string }> = [
    { n: 1, label: t("onboarding.step.structure") },
    { n: 2, label: t("onboarding.step.departments") },
    { n: 3, label: t("onboarding.step.staff") },
  ];

  const stepLabel = steps.find((s) => s.n === step)?.label ?? "";

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 space-y-6">
      <div className="grid gap-3">
        <div className="text-eyebrow">{t("onboarding.title")}</div>
        {/* The step progress is THE main progress of this screen — the one
            meter allowed to wear the Spectrum signature. */}
        <div className="flex items-center gap-3.5">
          <span className="text-eyebrow whitespace-nowrap text-fg-default">
            {t("onboarding.progress", { step: String(step), label: stepLabel })}
          </span>
          <div className="flex-1">
            <SpectrumBar value={(step / 3) * 100} height={4} />
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      {step === 1 && (
        <Card tone="subtle" padding="md" className="space-y-3">
          <p className="text-sm text-fg-muted">{t("onboarding.structure.subtitle")}</p>
          <FormField label={t("onboarding.field.hotelName")} required>
            <Input value={name} required onChange={(e) => setName(e.target.value)} />
          </FormField>
          <FormField label={t("onboarding.field.city")}>
            <Input value={city} onChange={(e) => setCity(e.target.value)} />
          </FormField>
          <FormField label={t("onboarding.field.address")}>
            <Input value={address} onChange={(e) => setAddress(e.target.value)} />
          </FormField>
          <FormField label={t("onboarding.field.phone")}>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </FormField>
          <div className="flex justify-end">
            <Button
              variant="primary"
              loading={busy}
              disabled={busy || !name}
              onClick={saveStructure}
            >
              {t("onboarding.next")}
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card tone="subtle" padding="md" className="space-y-3">
          <p className="text-sm text-fg-muted">{t("onboarding.departments.subtitle")}</p>
          <ul className="space-y-2">
            {departments.map((d) => (
              <li
                key={d.code}
                className="border border-border-default rounded-md px-3 py-2 flex items-center gap-2"
              >
                <Input
                  value={d.displayName}
                  aria-label={t("onboarding.departments.field.displayName")}
                  onChange={(e) => renameDept(d.code, e.target.value)}
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-danger"
                  disabled={busy}
                  onClick={() => void removeDept(d.code)}
                >
                  {t("onboarding.departments.remove")}
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex flex-col gap-2 sm:flex-row border-t border-border-default pt-3">
            <Input
              placeholder={t("onboarding.departments.field.code")}
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
            />
            <Input
              placeholder={t("onboarding.departments.field.displayName")}
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
            />
            <Button
              variant="secondary"
              disabled={busy || !newCode || !newDisplayName}
              onClick={() => void addDept()}
            >
              {t("onboarding.departments.add")}
            </Button>
          </div>
          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(1)}>
              {t("onboarding.back")}
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void saveDepartments()}>
              {t("onboarding.next")}
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card tone="subtle" padding="md" className="space-y-3">
          <p className="text-sm text-fg-muted">{t("onboarding.staff.subtitle")}</p>
          <FormField label={t("onboarding.field.email")}>
            <Input
              type="email"
              aria-label={t("onboarding.field.email")}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
            />
          </FormField>
          <FormField label={t("onboarding.staff.field.role")}>
            <Select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as (typeof ROLE_ORDER)[number])}
            >
              {ROLE_ORDER.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label={t("onboarding.staff.field.departments")}>
            <DepartmentMultiSelect
              hotelId={hotel?.id ?? ""}
              value={inviteDepartments}
              onChange={setInviteDepartments}
            />
          </FormField>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              loading={busy}
              disabled={busy || !inviteEmail}
              onClick={() => void sendInvite()}
            >
              {t("onboarding.staff.invite")}
            </Button>
          </div>
          {invited.length > 0 && (
            <ul className="text-mono-xs text-fg-muted space-y-1">
              {invited.map((email) => (
                <li key={email}>{t("onboarding.staff.invited", { email })}</li>
              ))}
            </ul>
          )}
          <div className="flex justify-between border-t border-border-default pt-3">
            <Button variant="ghost" onClick={() => setStep(2)}>
              {t("onboarding.back")}
            </Button>
            <Button variant="primary" loading={busy} onClick={() => void finish()}>
              {t("onboarding.finish")}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
